// Login em etapas: senha → (código do 2FA | cadastro do 2FA → códigos de
// recuperação) → troca da senha provisória, se for o caso.

import type { AuthResponse } from "@acervo/api-client";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { KeyRound, ShieldCheck } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { ErrorBox, Logo, SecretBox } from "../components/ui";
import { api } from "../lib/api";

type Step =
  | { kind: "password" }
  | { kind: "mfa"; token: string }
  | { kind: "setup"; token: string }
  | { kind: "codes"; codes: string[]; next: () => void };

export function LoginPage() {
  const navigate = useNavigate();
  const { voltar } = useSearch({ from: "/entrar" });
  const [step, setStep] = useState<Step>({ kind: "password" });

  const done = (r: AuthResponse) => {
    const go = () =>
      r.user?.must_change_password ? void navigate({ to: "/trocar-senha" }) : void navigate({ to: voltar ?? "/" });
    if (r.recovery_codes?.length) setStep({ kind: "codes", codes: r.recovery_codes, next: go });
    else go();
  };

  const handle = (r: AuthResponse) => {
    if (r.status === "mfa_required" && r.mfa_token) setStep({ kind: "mfa", token: r.mfa_token });
    else if (r.status === "mfa_setup_required" && r.mfa_token) setStep({ kind: "setup", token: r.mfa_token });
    else done(r);
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.1fr_1fr]">
      <aside className="relative hidden overflow-hidden border-r border-line lg:block">
        <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_20%_10%,rgba(245,165,36,0.18),transparent_60%),radial-gradient(60%_50%_at_90%_90%,rgba(245,165,36,0.08),transparent_60%)]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Logo className="text-2xl" />
          <div className="max-w-md">
            <h1 className="text-4xl leading-tight font-bold">Seus cursos, no seu ritmo, em qualquer tela.</h1>
            <p className="mt-4 text-muted">Continue de onde parou no computador ou na TV.</p>
          </div>
          <p className="text-xs text-muted">Área de alunos</p>
        </div>
      </aside>
      <section className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Logo className="mb-10 text-2xl lg:hidden" />
          {step.kind === "password" && <PasswordStep onResult={handle} />}
          {step.kind === "mfa" && (
            <MfaStep
              token={step.token}
              onResult={done}
              onRestart={() => setStep({ kind: "password" })}
            />
          )}
          {step.kind === "setup" && (
            <SetupStep token={step.token} onResult={done} onRestart={() => setStep({ kind: "password" })} />
          )}
          {step.kind === "codes" && <CodesStep codes={step.codes} onContinue={step.next} />}
        </div>
      </section>
    </div>
  );
}

function Title({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="mb-6">
      {icon && <div className="mb-3 text-accent [&_svg]:h-7 [&_svg]:w-7">{icon}</div>}
      <h2 className="text-2xl font-bold">{title}</h2>
      {children && <p className="mt-1 text-sm text-muted">{children}</p>}
    </div>
  );
}

function useSubmit<T>(fn: () => Promise<T>, onOk: (v: T) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onOk(await fn());
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

function PasswordStep({ onResult }: { onResult: (r: AuthResponse) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, submit } = useSubmit(() => api.login(email.trim(), password), onResult);
  return (
    <form onSubmit={submit} className="space-y-4">
      <Title title="Entrar">Use o e-mail e a senha da sua conta.</Title>
      <div>
        <label className="label" htmlFor="email">
          E-mail
        </label>
        <input
          id="email"
          className="field"
          type="email"
          autoComplete="username"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="password">
          Senha
        </label>
        <input
          id="password"
          className="field"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {error ? <ErrorBox error={error} /> : null}
      <button className="btn-primary w-full" disabled={busy}>
        {busy ? "Entrando…" : "Entrar"}
      </button>
    </form>
  );
}

function CodeInput({ value, onChange, recovery }: { value: string; onChange: (v: string) => void; recovery: boolean }) {
  return (
    <input
      id="code"
      className="field text-center font-mono text-xl tracking-[0.3em]"
      inputMode={recovery ? "text" : "numeric"}
      autoComplete="one-time-code"
      autoFocus
      required
      maxLength={recovery ? 11 : 6}
      pattern={recovery ? "[A-Za-z0-9]{5}-?[A-Za-z0-9]{5}" : "[0-9]{6}"}
      placeholder={recovery ? "XXXXX-XXXXX" : "000000"}
      aria-label={recovery ? "Código de recuperação" : "Código de 6 dígitos"}
      value={value}
      onChange={(e) => onChange(recovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))}
    />
  );
}

function MfaStep({
  token,
  onResult,
  onRestart,
}: {
  token: string;
  onResult: (r: AuthResponse) => void;
  onRestart: () => void;
}) {
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const { busy, error, submit } = useSubmit(() => api.verifyMfa(token, code.trim()), onResult);
  return (
    <form onSubmit={submit} className="space-y-4">
      <Title icon={<ShieldCheck />} title="Verificação em duas etapas">
        {recovery
          ? "Digite um dos códigos de recuperação que você guardou. Cada um só vale uma vez."
          : "Digite o código de 6 dígitos do seu app autenticador."}
      </Title>
      <CodeInput value={code} onChange={setCode} recovery={recovery} />
      {error ? <ErrorBox error={error} /> : null}
      <button className="btn-primary w-full" disabled={busy}>
        {busy ? "Verificando…" : "Confirmar"}
      </button>
      <div className="flex justify-between text-xs">
        <button
          type="button"
          className="text-muted hover:text-fg"
          onClick={() => {
            setRecovery((v) => !v);
            setCode("");
          }}
        >
          {recovery ? "Usar o app autenticador" : "Perdi o celular: usar código de recuperação"}
        </button>
        <button type="button" className="text-muted hover:text-fg" onClick={onRestart}>
          Voltar
        </button>
      </div>
    </form>
  );
}

function SetupStep({
  token,
  onResult,
  onRestart,
}: {
  token: string;
  onResult: (r: AuthResponse) => void;
  onRestart: () => void;
}) {
  const [secret, setSecret] = useState<{ secret: string; qr: string } | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [code, setCode] = useState("");
  const { busy, error, submit } = useSubmit(() => api.confirmMfa(token, code), onResult);

  useEffect(() => {
    let alive = true;
    setupOnce(token)
      .then(async (s) => {
        const qr = await QRCode.toDataURL(s.otpauth_url, { margin: 1, width: 220, color: { dark: "#0b0d10", light: "#ffffff" } });
        if (alive) setSecret({ secret: s.secret, qr });
      })
      .catch((e: unknown) => alive && setLoadError(e));
    return () => {
      alive = false;
    };
  }, [token]);

  if (loadError) {
    return (
      <div className="space-y-4">
        <ErrorBox error={loadError} />
        <button className="btn-ghost w-full" onClick={onRestart}>
          Voltar ao login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <Title icon={<ShieldCheck />} title="Proteja sua conta">
        Sua conta exige verificação em duas etapas. Escaneie o QR code com um app autenticador (Google Authenticator,
        Microsoft Authenticator, 1Password…).
      </Title>
      <div className="flex justify-center">
        {secret ? (
          <img src={secret.qr} alt="QR code para o app autenticador" className="h-[220px] w-[220px] rounded-lg" />
        ) : (
          <div className="h-[220px] w-[220px] animate-pulse rounded-lg bg-raised" />
        )}
      </div>
      {secret && (
        <details className="text-xs text-muted">
          <summary className="cursor-pointer">Não consigo escanear</summary>
          <p className="mt-2">Digite esta chave no app:</p>
          <code className="mt-1 block font-mono break-all text-fg">{secret.secret}</code>
        </details>
      )}
      <div>
        <label className="label" htmlFor="code">
          Código mostrado no app
        </label>
        <CodeInput value={code} onChange={setCode} recovery={false} />
      </div>
      {error ? <ErrorBox error={error} /> : null}
      <button className="btn-primary w-full" disabled={busy || !secret}>
        {busy ? "Ativando…" : "Ativar e entrar"}
      </button>
    </form>
  );
}

// Cada chamada ao setup gera um segredo novo no servidor. Se a tela pedir
// duas vezes (o React faz isso em desenvolvimento), as duas recebem a mesma
// resposta, e o QR code mostrado é o que o servidor espera.
const setups = new Map<string, ReturnType<typeof api.setupMfa>>();
function setupOnce(token: string) {
  let p = setups.get(token);
  if (!p) {
    p = api.setupMfa(token);
    setups.set(token, p);
    p.catch(() => setups.delete(token));
  }
  return p;
}

function CodesStep({ codes, onContinue }: { codes: string[]; onContinue: () => void }) {
  const [saved, setSaved] = useState(false);
  return (
    <div className="space-y-4">
      <Title icon={<KeyRound />} title="Guarde seus códigos de recuperação">
        Se perder o celular, cada código abaixo permite entrar uma vez. Eles não serão mostrados de novo.
      </Title>
      <SecretBox value={codes.join("\n")} hint="Guarde num gerenciador de senhas ou imprima." />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} className="accent-accent" />
        Guardei os códigos em lugar seguro
      </label>
      <button className="btn-primary w-full" disabled={!saved} onClick={onContinue}>
        Continuar
      </button>
    </div>
  );
}
